import Component from '@glimmer/component';
import { action } from '@ember/object';
import { isEmpty, isPresent } from '@ember/utils';
import { service } from '@ember/service';
import ContactsCache from 'donor-elf-web/services/contacts-cache';
import ContactModel from 'donor-elf-web/models/contact';
import { cached, tracked } from '@glimmer/tracking';
import { getChangesetErrorsForProperty } from 'donor-elf-web/lib/changeset-utils';

export interface FormUnderlinedSelectContactOnChangeOptions {
  selectedByKeyboardEnterEvent?: boolean; // true if the user selected the contact by hitting the enter key
}

interface ComponentArgs {
  changeset: any;
  property: string; // property value needs to be the contact id
  selectedContactId: any; // either @changeset and @property need to be supplied or @selectedContactId
  excludeContactId?: string; // contactId to exclude from the list
  placeholder?: string;
  disabled?: boolean; // default false
  showValidationErrors?: boolean; // default false
  initiallyOpened?: boolean; // open the dropdown initially. default is false
  horizontalPosition?: string; // default 'auto-left'
  dropdownMaxHeightClass?: string; // default 'tw-h-64'
  dropdownContentClass?: string; // 'tw-z-1100' can be used when the component is in a <Modal /> component
  onChange: (
    contactId: string,
    options?: FormUnderlinedSelectContactOnChangeOptions,
  ) => void;
}

export default class FormUnderlineSelectContactComponent extends Component<ComponentArgs> {
  @service declare contactsCache: ContactsCache;

  @tracked searchVal?: string;
  @tracked selectedIndex = 0;
  dropdown: any;

  get horizontalPosition() {
    return this.args.horizontalPosition || 'auto-left';
  }

  get initiallyOpened() {
    return this.args.initiallyOpened || false;
  }

  get dropdownMaxHeightClass() {
    return this.args.dropdownMaxHeightClass ?? 'tw-max-h-64';
  }

  get selectedContactId() {
    //                              don't let get() raise an error when @changeset or @property are null
    return (
      this.args.selectedContactId ??
      this.args.changeset?.get(this.args.property || '')
    );
  }

  get selectedContact(): ContactModel | undefined {
    let { selectedContactId } = this;
    if (selectedContactId) {
      return this.contactsCache.contactsById[selectedContactId];
    } else {
      return undefined;
    }
  }

  get notSelectedAndHasPlaceholder() {
    return isEmpty(this.selectedContact) && isPresent(this.args.placeholder);
  }

  @cached
  get filteredContacts() {
    let searchVal = this.searchVal || '';
    let contacts = this.contactsCache.sortedContacts;
    if (searchVal.length >= 3) {
      contacts = this.contactsCache.search(this.searchVal!);
    }
    if (this.args.excludeContactId) {
      contacts = contacts.filter((x) => x.id !== this.args.excludeContactId);
    }
    return contacts;
  }

  @cached
  get contactsToShow() {
    return this.filteredContacts.slice(0, 20);
  }

  get notShowingAllFilteredContacts() {
    return this.filteredContacts.length > this.contactsToShow.length;
  }

  get errorMessage() {
    return getChangesetErrorsForProperty(this.args.changeset, this.args.property)?.join(', ');
  }

  get showError() {
    return this.args.showValidationErrors && isPresent(this.errorMessage);
  }

  @action
  handleSearchInput(e: any) {
    this.searchVal = e.target!.value;
    if (this.selectedIndex !== 0) {
      this.selectedIndex = 0;
    }
  }

  @action
  handleSearchKeyDown(e: KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (this.selectedIndex + 1 < this.contactsToShow.length) {
        this.selectedIndex += 1;
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (this.selectedIndex > 0) {
        this.selectedIndex -= 1;
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      let selectedContact = this.contactsToShow[this.selectedIndex];
      if (selectedContact) {
        this.dropdown?.actions.close();
        this.setSelectedContactId(selectedContact.id, {
          selectedByKeyboardEnterEvent: true,
        });
      }
    }
  }

  @action
  setSelectedContactId(
    contactId: string,
    onChangeOptions?: FormUnderlinedSelectContactOnChangeOptions,
  ) {
    if (this.args.changeset) {
      this.args.changeset.set(this.args.property, contactId);
    }
    this.args.onChange?.(contactId, onChangeOptions);
  }

  @action
  handleDropdownOpened(dropdown: any) {
    this.dropdown = dropdown;
  }

  @action
  handleDropdownClosed() {
    this.selectedIndex = 0;
    this.searchVal = undefined;
  }
}
